import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBagShopping,
  faChartLine,
  faChartSimple,
  faCartPlus,
  faCartShopping,
  faCircleArrowRight,
  faCommentDots,
  faEnvelope,
  faIndianRupeeSign,
  faRotate,
  faStar,
  faTruckFast,
  faUserCheck,
  faWarehouse,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { apiUrl } from "../../config/config";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

const moneyFormatter = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 0,
});

const defaultTrend = [
  { label: "Jan", value: 12500, orders: 8 },
  { label: "Feb", value: 18200, orders: 12 },
  { label: "Mar", value: 14800, orders: 9 },
  { label: "Apr", value: 22600, orders: 15 },
  { label: "May", value: 30100, orders: 18 },
  { label: "Jun", value: 27800, orders: 17 },
];

const defaultUserGrowth = [
  { label: "Jan", value: 0 },
  { label: "Feb", value: 0 },
  { label: "Mar", value: 0 },
  { label: "Apr", value: 0 },
  { label: "May", value: 0 },
  { label: "Jun", value: 0 },
];

const getNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (value) => `₹${moneyFormatter.format(getNumber(value))}`;

const formatDate = (value) => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const normalizeSeries = (items, fallback = []) => {
  const source = Array.isArray(items) && items.length > 0 ? items : fallback;
  return source.map((item) => ({
    label: item.label || item.month || item.name || item.status || "-",
    value: getNumber(item.value ?? item.amount ?? item.total ?? item.count),
    orders: getNumber(item.orders ?? item.order_count ?? item.count),
  }));
};

const normalizeTopOrderRows = (items) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => ({
      label: item.label || item.state || item.city || item.day || item.weekday || item.name || "-",
      value: getNumber(item.value ?? item.orders ?? item.order_count ?? item.count ?? item.total),
    }))
    .filter((item) => item.value > 0)
    .slice(0, 5);
};

const formatOrderSourceLabel = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (["mobile", "app", "ios", "android"].includes(normalized)) {
    return "App";
  }
  if (["website", "web"].includes(normalized)) {
    return "Website";
  }
  return value || "Unknown";
};

const normalizeOrderSourceSummary = (dashboardData) => {
  const sourceRows =
    dashboardData.ordersourcesummary ||
    dashboardData.orderSourceSummary ||
    dashboardData.order_source_summary;

  if (Array.isArray(sourceRows) && sourceRows.length > 0) {
    return sourceRows.map((item) => ({
      label: formatOrderSourceLabel(item.label || item.source || item.orderfrom || item.order_from || item.name),
      value: getNumber(item.value ?? item.count ?? item.total),
    }));
  }

  const websiteOrders = getNumber(
    dashboardData.websiteordercount ?? dashboardData.webordercount ?? dashboardData.websiteorders ?? dashboardData.weborders
  );
  const appOrders = getNumber(
    dashboardData.appordercount ?? dashboardData.mobileordercount ?? dashboardData.apporders ?? dashboardData.mobileorders
  );

  if (websiteOrders > 0 || appOrders > 0) {
    return [
      { label: "Website", value: websiteOrders },
      { label: "App", value: appOrders },
    ];
  }

  return [];
};

const LineChart = ({ data, fallback = defaultTrend, formatValue = formatCurrency, gradientId = "dashboardRevenueFill", title = "Trend chart", tone = "#f43f5e" }) => {
  const series = normalizeSeries(data, fallback);
  const max = Math.max(...series.map((item) => item.value), 1);
  const points = series
    .map((item, index) => {
      const x = series.length === 1 ? 50 : (index / (series.length - 1)) * 100;
      const y = 92 - (item.value / max) * 78;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <div className="admin-linechart">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-label={title}>
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={tone} stopOpacity="0.32" />
            <stop offset="100%" stopColor={tone} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <polyline points={`0,96 ${points} 100,96`} fill={`url(#${gradientId})`} stroke="none" />
        <polyline points={points} fill="none" stroke={tone} strokeWidth="3" strokeLinecap="round" />
      </svg>
      <div className="admin-linechart-labels">
        {series.map((item, index) => (
          <span key={`${item.label}-${index}`}>
            <strong>{formatValue(item.value)}</strong>
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const CustomerOrderSummary = ({ data, sourceData = [] }) => {
  const series = normalizeSeries(data, [
    { label: "New Users", value: 0 },
    { label: "Repeated Users", value: 0 },
  ]);
  const total = series.reduce((sum, item) => sum + item.value, 0);
  const sourceTotal = sourceData.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="dashboard-customer-summary">
      <div className="dashboard-customer-total">
        <span>Total Customer Orders</span>
        <strong>{total}</strong>
      </div>
      {series.map((item, index) => {
        const percentage = total > 0 ? Math.round((item.value / total) * 100) : 0;
        return (
          <div className="dashboard-customer-row" key={`${item.label}-${index}`}>
            <div>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
            </div>
            <div className="dashboard-customer-track">
              <span style={{ width: `${Math.max(percentage, item.value > 0 ? 8 : 0)}%` }} />
            </div>
            <small>{percentage}%</small>
          </div>
        );
      })}
      {sourceData.length > 0 ? (
        <div className="dashboard-source-summary">
          <span>Order Source</span>
          {sourceData.map((item, index) => {
            const percentage = sourceTotal > 0 ? Math.round((item.value / sourceTotal) * 100) : 0;
            return (
              <div className="dashboard-source-row" key={`${item.label}-${index}`}>
                <strong>{item.label}</strong>
                <small>
                  {item.value} orders · {percentage}%
                </small>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

const TopOrdersChart = ({ title, subtitle, data, emptyText }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="dashboard-panel dashboard-top-orders-panel">
      <div className="dashboard-panel-head compact">
        <div>
          <p>{subtitle}</p>
          <h3>{title}</h3>
        </div>
        <FontAwesomeIcon icon={faChartSimple} />
      </div>
      <div className="dashboard-top-orders-list">
        {data.length > 0 ? (
          data.map((item, index) => {
            const percentage = total > 0 ? Math.round((item.value / total) * 100) : 0;
            return (
              <div className="dashboard-top-orders-row" key={`${title}-${item.label}-${index}`}>
                <div className="dashboard-top-orders-meta">
                  <strong>{item.label}</strong>
                  <span>{item.value} orders</span>
                </div>
                <div className="dashboard-top-orders-track" aria-label={`${item.label} ${percentage}%`}>
                  <span style={{ width: `${Math.max(percentage, item.value > 0 ? 6 : 0)}%` }} />
                </div>
                <small>{percentage}%</small>
              </div>
            );
          })
        ) : (
          <div className="dashboard-empty-state">{emptyText}</div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ card }) => {
  const content = (
    <div className={`dashboard-summary-card ${card.tone}`}>
      <div>
        <p className="dashboard-summary-label">{card.label}</p>
        <p className="dashboard-summary-value">{card.value}</p>
        <span>{card.note}</span>
      </div>
      <FontAwesomeIcon icon={card.icon} className="dashboard-summary-icon" />
      <div className="dashboard-summary-action">
        <span>{card.action || "More Info"}</span>
        <FontAwesomeIcon icon={faCircleArrowRight} />
      </div>
    </div>
  );

  if (card.onClick) {
    return (
      <button type="button" className="dashboard-card-button" onClick={card.onClick}>
        {content}
      </button>
    );
  }

  return (
    <Link to={card.link} className="dashboard-card-link">
      {content}
    </Link>
  );
};

const RecentList = ({ title, subtitle, icon, link, items, emptyText, type }) => (
  <div className="dashboard-panel dashboard-recent-panel">
    <div className="dashboard-panel-head compact">
      <div>
        <p>{subtitle}</p>
        <h3>{title}</h3>
      </div>
      <Link to={link} className="dashboard-panel-link">
        <FontAwesomeIcon icon={icon} />
      </Link>
    </div>
    <div className="dashboard-recent-list">
      {items.length > 0 ? (
        items.map((item, index) => {
          const name = item.name || item.Name || item.user_name || "Customer";
          const message = item.content || item.message || item.feedback || "";
          const email = item.email || item.Email || "";
          const phone = item.phone || item.Mobile || "";
          const date = formatDate(item.created_at);
          return (
            <div className="dashboard-recent-item" key={`${type}-${item.id || index}`}>
              <div className="dashboard-recent-avatar">{name.slice(0, 1).toUpperCase()}</div>
              <div>
                <div className="dashboard-recent-topline">
                  <strong>{name}</strong>
                  <span>{date}</span>
                </div>
                <p>{message}</p>
                <small>{email || phone || (type === "feedback" ? "Feedback received" : "Contact enquiry")}</small>
              </div>
            </div>
          );
        })
      ) : (
        <div className="dashboard-empty-state">{emptyText}</div>
      )}
    </div>
  </div>
);

const AdminDashboard = () => {
  const userdata = useSelector((store) => store.userReducer.userdata);
  const [showloding, setshowloding] = useState(false);
  const [dashboardData, setDashboardData] = useState({});
  const navigate = useNavigate();

  const handleclickunshipporders = useCallback(() => {
    navigate("/admin/all-orders", { state: { filter: "Unshipped" } });
  }, [navigate]);

  const fetchdashboardcounts = useCallback(async () => {
    setshowloding(true);
    const headers = {
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + userdata?.token,
      },
    };

    await axios
      .get(apiUrl + "getdashboardcounts", headers)
      .then((res) => {
        if (res.data.status === "success") {
          setDashboardData(res.data);
        }
      })
      .catch((err) => {
        alert(err.message);
      });
    setshowloding(false);
  }, [userdata?.token]);

  useEffect(() => {
    fetchdashboardcounts();
  }, [fetchdashboardcounts]);

  const summaryCards = useMemo(
    () => [
      {
        label: "Products",
        value: getNumber(dashboardData.productcount),
        note: `${getNumber(dashboardData.activeproducts)} active listings`,
        icon: faBagShopping,
        link: "/admin/product",
        tone: "tone-cyan",
      },
      {
        label: "Users",
        value: getNumber(dashboardData.userscount),
        note: `${getNumber(dashboardData.newusers)} new this month`,
        icon: faUserCheck,
        link: "/admin/users",
        tone: "tone-green",
      },
      {
        label: "New Orders",
        value: getNumber(dashboardData.neworders),
        note: "Orders placed this month",
        icon: faCartPlus,
        link: "/admin/all-orders",
        tone: "tone-gold",
      },
      {
        label: "Unshipped Orders",
        value: getNumber(dashboardData.unshippedcount),
        note: "Needs fulfilment attention",
        icon: faTruckFast,
        onClick: handleclickunshipporders,
        tone: "tone-blue",
      },
      {
        label: "All Orders",
        value: getNumber(dashboardData.allorders),
        note: `${getNumber(dashboardData.failedorders)} payment pending`,
        icon: faCartShopping,
        link: "/admin/all-orders",
        tone: "tone-red",
      },
      {
        label: "Order Value",
        value: formatCurrency(dashboardData.totalamount),
        note: `${formatCurrency(dashboardData.monthamount)} this month`,
        icon: faIndianRupeeSign,
        link: "/admin/all-orders",
        tone: "tone-rose",
      },
      {
        label: "Feedback",
        value: getNumber(dashboardData.feedbackcount),
        note: "Received in the past 2 days",
        icon: faCommentDots,
        link: "/admin/userFeedbackList",
        tone: "tone-teal",
      },
      {
        label: "Stock Watch",
        value: getNumber(dashboardData.lowstockcount),
        note: "Products at low or zero stock",
        icon: faWarehouse,
        link: "/admin/product",
        tone: "tone-slate",
      },
    ],
    [dashboardData, handleclickunshipporders]
  );

  const revenueTrend = normalizeSeries(dashboardData.revenuetrend, defaultTrend);
  const customerOrderSummary = normalizeSeries(dashboardData.customerordersummary, [
    { label: "New User Orders", value: getNumber(dashboardData.newuserordercount) },
    { label: "Repeat Orders", value: getNumber(dashboardData.repeateduserordercount) },
  ]);
  const orderSourceSummary = normalizeOrderSourceSummary(dashboardData);
  const userGrowthTrend = normalizeSeries(dashboardData.usergrowthtrend, defaultUserGrowth);
  const topStateOrders = normalizeTopOrderRows(dashboardData.topstateorders || dashboardData.topstatesorders);
  const topCityOrders = normalizeTopOrderRows(dashboardData.topcityorders || dashboardData.topcitiesorders);
  const topWeekdayOrders = normalizeTopOrderRows(dashboardData.topweekdayorders || dashboardData.topdayorders);
  const recentFeedbacks = Array.isArray(dashboardData.recentfeedbacks) ? dashboardData.recentfeedbacks : [];
  const recentContacts = Array.isArray(dashboardData.recentcontacts) ? dashboardData.recentcontacts : [];

  return (
    <div className="adminmainpagelayout dashboard-modern">
      <div className="dashboard-hero">
        <div>
          <p className="dashboard-kicker">Admin Overview</p>
          <h2>Blisstainment command center</h2>
          <span>Revenue movement, fulfilment load, recent feedback, and contact form activity in one focused view.</span>
        </div>
        <button className="dashboard-refresh-btn" type="button" onClick={fetchdashboardcounts} disabled={showloding}>
          <FontAwesomeIcon icon={faRotate} className={showloding ? "dashboard-spin" : ""} />
          {showloding ? "Refreshing" : "Refresh"}
        </button>
      </div>

      <div className="dashboard-summary-grid">
        {summaryCards.map((card) => (
          <StatCard key={card.label} card={card} />
        ))}
      </div>

      <div className="row dashboard-analytics-row">
        <div className="col-lg-8">
          <div className="dashboard-panel">
            <div className="dashboard-panel-head">
              <div>
                <p>Revenue Summary</p>
                <h3>{formatCurrency(dashboardData.totalamount)}</h3>
              </div>
              <FontAwesomeIcon icon={faChartLine} />
            </div>
            <LineChart data={revenueTrend} />
            <div className="dashboard-insight-strip">
              <div>
                <span>Monthly Revenue</span>
                <strong>{formatCurrency(dashboardData.monthamount)}</strong>
              </div>
              <div>
                <span>Average Order</span>
                <strong>{formatCurrency(dashboardData.averageordervalue)}</strong>
              </div>
              <div>
                <span>Monthly Orders</span>
                <strong>{getNumber(dashboardData.neworders)}</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="dashboard-panel dashboard-status-panel">
            <div className="dashboard-panel-head">
              <div>
                <p>Order Summary</p>
                <h3>Customer orders</h3>
              </div>
              <FontAwesomeIcon icon={faUserCheck} />
            </div>
            <CustomerOrderSummary data={customerOrderSummary} sourceData={orderSourceSummary} />
          </div>
        </div>
      </div>

      <div className="row dashboard-analytics-row">
        <div className="col-lg-12">
          <div className="dashboard-panel dashboard-user-growth-panel">
            <div className="dashboard-panel-head">
              <div>
                <p>User Growth Rate</p>
                <h3>Last 6 months</h3>
              </div>
              <FontAwesomeIcon icon={faChartSimple} />
            </div>
            <LineChart
              data={userGrowthTrend}
              fallback={defaultUserGrowth}
              formatValue={(value) => `${getNumber(value)} users`}
              gradientId="dashboardUserGrowthFill"
              title="Monthly user growth chart"
              tone="#2563eb"
            />
          </div>
        </div>
      </div>

      <div className="row dashboard-analytics-row">
        <div className="col-lg-4">
          <TopOrdersChart
            title="Top 5 States"
            subtitle="Highest Orders"
            data={topStateOrders}
            emptyText="No state order data found."
          />
        </div>
        <div className="col-lg-4">
          <TopOrdersChart
            title="Top 5 Cities"
            subtitle="Highest Orders"
            data={topCityOrders}
            emptyText="No city order data found."
          />
        </div>
        <div className="col-lg-4">
          <TopOrdersChart
            title="Top 5 Weekdays"
            subtitle="Highest Orders"
            data={topWeekdayOrders}
            emptyText="No weekday order data found."
          />
        </div>
      </div>

      <div className="row dashboard-analytics-row">
        <div className="col-lg-6">
          <RecentList
            title="Recent Feedback"
            subtitle="Customer Voice"
            icon={faStar}
            link="/admin/userFeedbackList"
            items={recentFeedbacks}
            emptyText="No recent feedback found."
            type="feedback"
          />
        </div>

        <div className="col-lg-6">
          <RecentList
            title="Contact Forms"
            subtitle="Latest Enquiries"
            icon={faEnvelope}
            link="/admin/contactform"
            items={recentContacts}
            emptyText="No contact enquiries found."
            type="contact"
          />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
